import { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ZoomOutMap } from '@mui/icons-material';
import type { DirectShearReading } from '../../../types/directShearReading';
import { isResultReading } from '../../../utils/directShearReadings';
import type { AnalysisData, AnalysisRanges } from './analysisTypes';
import type { MarkerRow } from './chartConfig';
import { CHART_COLORS } from './chartConfig';
import { useStressChartOptions } from './charts/StressChart';
import { useFrictionAngleChartOptions } from './charts/FrictionAngleChart';
import { useDisplacementDilationChartOptions } from './charts/DisplacementDilationChart';
import { SyncedChart } from './SyncedChart';
import { useSyncedCharts } from './useSyncedCharts';
import { PeakResidualInputs } from './PeakResidualInputs';
import { StageResultsTable } from './StageResultsTable';

interface StageAnalysisProps {
  readings: DirectShearReading[];
  stageNumber: number;
  analysis: AnalysisData;
  onRangesChange: (ranges: AnalysisRanges) => void;
  // "Calculate" (renamed from "Highlight", 2026-06-12 review) — computes &
  // persists Peak/Residual across all stages.
  onCalculate: () => void;
  saving: boolean;
  // Dilation range (passed through to PeakResidualInputs)
  dilationRange?: string;
  onDilationRangeChange?: (v: string) => void;
  onApplyRecalculate?: () => void;
  recalculating?: boolean;
  recalcProgress?: string;
  readOnly?: boolean;
}

/** Peak: max Corrected Friction Angle in the range. Matches the backend. */
function findPeakRow(readings: DirectShearReading[], from: number, to: number): MarkerRow {
  const inRange = readings.filter(
    (r) => r.HorizontalDisplacementCumulative != null
      && r.HorizontalDisplacementCumulative >= from
      && r.HorizontalDisplacementCumulative <= to
      && r.CorrectedFrictionAngle != null,
  );
  if (inRange.length === 0) return null;
  return inRange.reduce((max, r) =>
    (r.CorrectedFrictionAngle ?? 0) > (max.CorrectedFrictionAngle ?? 0) ? r : max,
  );
}

/** Residual row is threaded through the per-stage chart helpers but none of
 *  them actually render it (buildMarkerSeries ignores the second arg). Kept
 *  for API compat; picks any row in range. */
function findResidualRow(readings: DirectShearReading[], from: number, to: number): MarkerRow {
  const inRange = readings.filter(
    (r) => r.HorizontalDisplacementCumulative != null
      && r.HorizontalDisplacementCumulative >= from
      && r.HorizontalDisplacementCumulative <= to
      && r.CorrectedShearStress != null,
  );
  return inRange[0] ?? null;
}

export const StageAnalysis = ({
  readings,
  stageNumber,
  analysis,
  onRangesChange,
  onCalculate,
  saving,
  dilationRange, onDilationRangeChange, onApplyRecalculate, recalculating, recalcProgress,
  readOnly,
}: StageAnalysisProps) => {
  // Seating-phase rows (IsSeating, mig 108) are excluded from every per-stage
  // chart and the peak/residual pickers — only result-table rows are plotted
  // (see utils/directShearReadings.ts).
  const calculatedReadings = useMemo(
    () => readings.filter((r) => r.CorrectedShearStress != null && isResultReading(r)),
    [readings],
  );

  const { peakRow, residualRow } = useMemo(() => {
    let peak: MarkerRow = null;
    let residual: MarkerRow = null;

    if (analysis.peakRangeFrom != null && analysis.peakRangeTo != null) {
      peak = findPeakRow(calculatedReadings, analysis.peakRangeFrom, analysis.peakRangeTo);
    }
    if (analysis.residualRangeFrom != null && analysis.residualRangeTo != null) {
      residual = findResidualRow(calculatedReadings, analysis.residualRangeFrom, analysis.residualRangeTo);
    }

    return { peakRow: peak, residualRow: residual };
  }, [calculatedReadings, analysis]);

  const ranges: AnalysisRanges = {
    peakRangeFrom: analysis.peakRangeFrom,
    peakRangeTo: analysis.peakRangeTo,
    residualRangeFrom: analysis.residualRangeFrom,
    residualRangeTo: analysis.residualRangeTo,
  };

  // Chart options hooks
  const stressOptions = useStressChartOptions({
    readings: calculatedReadings, analysis, peakRow, residualRow,
  });
  const frictionOptions = useFrictionAngleChartOptions({
    readings: calculatedReadings, analysis, peakRow, residualRow,
  });
  const dispOptions = useDisplacementDilationChartOptions({
    readings: calculatedReadings,
    title: 'Vertical Displacement (Cumulative)',
    yLabel: 'Vertical Displacement (mm)',
    yField: 'VerticalDisplacementCumulative',
    color: CHART_COLORS.blue,
    ranges, peakRow, residualRow,
  });
  const dilOptions = useDisplacementDilationChartOptions({
    readings: calculatedReadings,
    title: 'Dilation Angle',
    yLabel: 'Dilation Angle (°)',
    yField: 'DilationAngle',
    color: CHART_COLORS.red,
    ranges, peakRow, residualRow,
  });

  // Synchronized chart controls
  const { registerChart, syncCrosshair, hideCrosshairs, syncZoom, resetAllZoom } = useSyncedCharts();

  if (calculatedReadings.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No calculated data for Stage {stageNumber}. Run calculations on the Result Entry tab first.
        </Typography>
      </Box>
    );
  }

  const chartProps = {
    onSyncCrosshair: syncCrosshair,
    onHideCrosshairs: hideCrosshairs,
    onSyncZoom: syncZoom,
    registerChart,
  };

  return (
    <Box>
      <PeakResidualInputs
        ranges={ranges}
        onChange={onRangesChange}
        onCalculate={onCalculate}
        saving={saving}
        stageNumber={stageNumber}
        dilationRange={dilationRange}
        onDilationRangeChange={onDilationRangeChange}
        onApplyRecalculate={onApplyRecalculate}
        recalculating={recalculating}
        recalcProgress={recalcProgress}
        readOnly={readOnly}
      />

      <StageResultsTable analysis={analysis} stageNumber={stageNumber} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ZoomOutMap sx={{ fontSize: 16 }} />}
          onClick={resetAllZoom}
          sx={{ textTransform: 'none', fontSize: '0.78rem' }}
        >
          Reset All Zoom
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
        <SyncedChart options={stressOptions} chartIndex={0} {...chartProps} />
        <SyncedChart options={frictionOptions} chartIndex={1} {...chartProps} />
        <SyncedChart options={dispOptions} chartIndex={2} {...chartProps} />
        <SyncedChart options={dilOptions} chartIndex={3} {...chartProps} />
      </Box>
    </Box>
  );
};
